/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import Select, {
  ActionMeta,
  components,
  MenuPlacement,
  MenuPosition,
  OnChangeValue,
} from 'react-select';
import { useTheme } from 'styled-components';
import staticTheme from 'src/theme/theme';
import { SelectWrapper } from './SelectDropdownGeneric.styles';

type Option = {
  label: string | number;
  value: string | number;
};
export interface SelectDropdownGenericProps<IsMulti extends boolean> {
  options: Array<Option>;
  defaultValue?: Option | Array<Option>;
  value?: Option | Array<Option>;
  label: string;
  customStyle?: {
    [name: string]: React.CSSProperties;
  };
  filledInDropdownIndicator?: boolean;
  isMulti?: IsMulti;
  placeholder?: string;
  withBorder?: boolean;
  onChange: (value: OnChangeValue<Option, IsMulti>) => void;
  menuPlacement?: MenuPlacement;
  menuPosition?: MenuPosition;
  className?: string;
  classNamePrefix?: string;
  hasSelectAllOption?: boolean;
  defaultAllSelected?: boolean;
  dataQa?: string;
  zIndex?: number;
}

const menuStyle = {
  minWidth: '180px',
  fontSize: '14px',
  fontWeight: '400',
  fontFamily: `${staticTheme.typography.fontFamily}`,
  border: `1px solid ${staticTheme.newPalette.neutral.N400}`,
  backgroundColor: staticTheme.newPalette.neutral.N0,
  borderRadius: `5px`,
  textAlign: 'left',
} as const;

const controlStyle = {
  display: 'flex',
  flexDirection: 'row',
  fontSize: '13px',
  fontWeight: '500',
  fontFamily: `${staticTheme.typography.fontFamily}`,
  width: '190px',
  boxShadow: 'none',
  border: `1px solid ${staticTheme.newPalette.neutral.N400}`,
  backgroundColor: staticTheme.newPalette.neutral.N0,
  borderRadius: `5px`,
} as const;

/**
 * @deprecated Please use DropdownSelect in atoms/DropdownSelect/DropdownSelect.tsx
 */
export function SelectDropdownGeneric<IsMulti extends boolean = false>({
  placeholder = 'Show/Hide Columns',
  withBorder = true,
  customStyle = {},
  filledInDropdownIndicator = false,
  menuPlacement = 'auto',
  menuPosition = 'absolute',
  className,
  classNamePrefix,
  hasSelectAllOption = false,
  defaultAllSelected = false,
  ...props
}: SelectDropdownGenericProps<IsMulti>) {
  const [allSelected, setAllSelected] = useState<boolean>(defaultAllSelected);
  const theme = useTheme();

  const withBorderDefaultStyles = {
    menu: (provided: any, state: any) => ({
      ...provided,
      width: state.selectProps.width,
      ...menuStyle,
      onChange: props.onChange,
      ...customStyle.menuStyle,
    }),
    control: () => ({
      ...controlStyle,
      ...customStyle.controlStyle,
    }),
    dropdownIndicator: (base: any, state: any) => ({
      ...base,
      transition: 'all .2s ease',
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : null,
      ...customStyle.dropdownIndicator,
    }),
    option: (styles: any, { isFocused, isSelected }: any) => {
      return {
        ...styles,
        backgroundColor: theme.isVaultVariant
          ? isFocused || isSelected
            ? staticTheme.newPalette.turquoise.T300
            : staticTheme.newPalette.neutral.N0
          : isFocused
            ? theme.atlas.semantic.color.action.secondary.hoverFill
            : theme.atlas.semantic.color.surface.default,
        color: theme.isVaultVariant
          ? isFocused || isSelected
            ? staticTheme.newPalette.neutral.N0
            : staticTheme.newPalette.neutral.N800
          : theme.atlas.semantic.color.type.default,
        ...customStyle.optionStyle,
      };
    },
    singleValue: (provided: any) => ({
      ...provided,
      ...customStyle.singleValue,
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      fontFamily: theme.typography.fontFamily,
      ...customStyle.valueContainer,
    }),
    placeholder: (provided: any) => ({
      ...provided,
      ...customStyle.placeholder,
      color: theme.newPalette.neutral.N800,
    }),
  };

  const noBorderDefaultStyles: any = {
    ...withBorderDefaultStyles,
    control: () => ({
      ...controlStyle,
      ...customStyle.controlStyle,
      border: 'none',
    }),
  };

  const onChangeWithSelectAllOption = (
    selectedOptions: OnChangeValue<Option, IsMulti>,
    meta: ActionMeta<Option>,
  ) => {
    if (selectedOptions && Array.isArray(selectedOptions)) {
      if (meta.action === 'select-option' && meta.option?.value === 'all') {
        setAllSelected(true);
        return props.onChange(
          props.options as unknown as OnChangeValue<Option, IsMulti>,
        );
      }

      if (meta.action === 'deselect-option' && meta.option?.value === 'all') {
        setAllSelected(false);
        return props.onChange([] as unknown as OnChangeValue<Option, IsMulti>);
      }

      setAllSelected(
        meta.action === 'select-option' &&
          selectedOptions.length === props.options.length,
      );
      const newOptions = selectedOptions.filter(
        (option) => option.value !== 'all',
      );
      props.onChange(newOptions as any as OnChangeValue<Option, IsMulti>);
    }
  };

  const selectAllOptions = [
    { label: 'Select All', value: 'all' },
    ...props.options,
  ];

  return (
    <SelectWrapper data-testid={props.dataQa} zIndex={props.zIndex}>
      <Select
        styles={withBorder ? withBorderDefaultStyles : noBorderDefaultStyles}
        className={className}
        classNamePrefix={classNamePrefix}
        closeMenuOnSelect={!props.isMulti}
        defaultValue={
          hasSelectAllOption && defaultAllSelected
            ? selectAllOptions
            : props.defaultValue
        }
        value={
          hasSelectAllOption && allSelected ? selectAllOptions : props.value
        }
        onChange={
          hasSelectAllOption ? onChangeWithSelectAllOption : props.onChange
        }
        options={hasSelectAllOption ? selectAllOptions : props.options}
        isMulti={props.isMulti}
        isClearable={false}
        isSearchable={false}
        menuPlacement={menuPlacement}
        menuPosition={menuPosition}
        placeholder={placeholder}
        controlShouldRenderValue={props.isMulti ? false : true}
        components={{
          IndicatorSeparator: () => null,
          Option: props.isMulti
            ? (props: any) => (
                <components.Option {...props}>
                  <input
                    type="checkbox"
                    checked={props.isSelected}
                    data-testid={`multi-select-option`}
                    value={props.label}
                    readOnly
                  />{' '}
                  <label>{props.label}</label>
                </components.Option>
              )
            : (props: any) => (
                <div data-testid={`${props.label}-select`}>
                  <components.Option {...props} />
                </div>
              ),
          DropdownIndicator: filledInDropdownIndicator
            ? (props: any) => (
                <components.DropdownIndicator
                  width={'7px'}
                  height={'4px'}
                  {...props}
                >
                  <img src="/assets/fullArrowDown.svg" alt="Expand Dropdown" />
                </components.DropdownIndicator>
              )
            : components.DropdownIndicator,
        }}
        hideSelectedOptions={false}
      />
    </SelectWrapper>
  );
}
